'use client';

import { useEffect, useState } from 'react';
import {
  resolveThemePreference,
  THEME_STORAGE_KEY,
  type ThemeChangeDetail,
  type ThemeMode,
  type ThemePreference,
} from '@/lib/theme';

type ThemeChangeEvent = CustomEvent<ThemeChangeDetail>;

function getStoredPreference(): ThemePreference {
  const storedPreference = window.localStorage.getItem(THEME_STORAGE_KEY);

  return storedPreference === 'light' || storedPreference === 'system' ? storedPreference : 'dark';
}

function getSystemTheme(mediaQuery?: MediaQueryList): ThemeMode {
  const source = mediaQuery ?? window.matchMedia('(prefers-color-scheme: light)');
  return source.matches ? 'light' : 'dark';
}

function applyThemePreference(preference: ThemePreference, mediaQuery?: MediaQueryList) {
  const resolvedTheme = resolveThemePreference(preference, getSystemTheme(mediaQuery) === 'light');
  document.documentElement.dataset.themePreference = preference;
  document.documentElement.dataset.theme = resolvedTheme;

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Ignore storage errors and keep the current in-memory preference.
  }

  window.dispatchEvent(
    new CustomEvent<ThemeChangeDetail>('cleanor-theme-change', {
      detail: { preference, theme: resolvedTheme },
    }),
  );

  return resolvedTheme;
}

const themeOptions: Array<{
  value: ThemePreference;
  label: string;
  description: string;
}> = [
  { value: 'light', label: 'Light', description: 'Always light' },
  { value: 'system', label: 'Auto', description: 'Follow the OS' },
  { value: 'dark', label: 'Dark', description: 'Always dark' },
];

export default function ThemePreferencePanel() {
  const [preference, setPreference] = useState<ThemePreference>('dark');
  const [resolvedTheme, setResolvedTheme] = useState<ThemeMode>('dark');

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');

    function syncTheme(nextPreference?: ThemePreference) {
      const currentPreference = nextPreference ?? getStoredPreference();
      setPreference(currentPreference);
      setResolvedTheme(resolveThemePreference(currentPreference, mediaQuery.matches));
    }

    function handleThemeChange(event: Event) {
      const detail = (event as ThemeChangeEvent).detail;

      if (!detail) {
        syncTheme();
        return;
      }

      setPreference(detail.preference);
      setResolvedTheme(detail.theme);
    }

    function handleStorage(event: StorageEvent) {
      if (event.key !== THEME_STORAGE_KEY) {
        return;
      }

      syncTheme(
        event.newValue === 'light' || event.newValue === 'system' ? event.newValue : 'dark',
      );
    }

    function handleSystemThemeChange() {
      if (getStoredPreference() !== 'system') {
        return;
      }

      syncTheme('system');
      applyThemePreference('system', mediaQuery);
    }

    syncTheme();
    window.addEventListener('cleanor-theme-change', handleThemeChange as EventListener);
    window.addEventListener('storage', handleStorage);
    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      window.removeEventListener('cleanor-theme-change', handleThemeChange as EventListener);
      window.removeEventListener('storage', handleStorage);
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  function handlePreferenceChange(nextPreference: ThemePreference) {
    const nextTheme = applyThemePreference(nextPreference);
    setPreference(nextPreference);
    setResolvedTheme(nextTheme);
  }

  return (
    <section className="account-tab-section">
      <div className="account-section-heading">
        <h3>Appearance</h3>
      </div>

      <div className="account-theme-preview-grid" role="group" aria-label="Theme preference">
        {themeOptions.map((option) => {
          const isActive = preference === option.value;
          const previewMode =
            option.value === 'system' ? resolvedTheme : option.value === 'light' ? 'light' : 'dark';

          return (
            <button
              aria-pressed={isActive}
              className={`account-theme-preview-card ${isActive ? 'account-theme-preview-card-active' : ''}`}
              key={option.value}
              onClick={() => handlePreferenceChange(option.value)}
              type="button"
            >
              <span
                className={`account-theme-preview-surface account-theme-preview-surface-${previewMode}`}
              >
                <span className="account-theme-preview-topbar">
                  <span className="account-theme-preview-pill" />
                </span>
                <span className="account-theme-preview-content">
                  <span className="account-theme-preview-line account-theme-preview-line-strong" />
                  <span className="account-theme-preview-line" />
                  <span className="account-theme-preview-panel" />
                </span>
              </span>
              <span className="account-theme-preview-copy">
                <strong>{option.label}</strong>
                <span>{option.description}</span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
